var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
var __decorateParam = (index, decorator) => (target, key) => decorator(target, key, index);

// db/files.ts
import { onlyClient } from "@adornis/base/decorators/onlyClient.js";
import { onlyServer } from "@adornis/base/decorators/onlyServer.js";
import { A } from "@adornis/base/env-info.js";
import { logger } from "@adornis/base/logging.js";
import { Int } from "@adornis/baseql/baseqlTypes.js";
import { BaseQLClient } from "@adornis/baseql/client/baseqlClient.js";
import { Arg, Entity, Field, Mutation } from "@adornis/baseql/decorators.js";
import { AdornisEntity } from "@adornis/baseql/entities/adornisEntity.js";
import { constructValue } from "@adornis/baseql/entities/construct.js";
import { MongoEntity } from "@adornis/baseql/entities/mongoEntity.js";
import { getRawCollection } from "@adornis/baseql/server/collections.js";
import { selectionSet } from "@adornis/baseql/utils/queryGeneration.js";
import { validate } from "@adornis/validation/decorators.js";
import { nonOptional } from "@adornis/validation/functions/nonOptional.js";
import { createReadStream } from "fs";
import { lstat, mkdir, stat, unlink, writeFile } from "fs/promises";
import { dirname, join as nodeJoin } from "node:path";
import { basename } from "path";
import { pipeline } from "stream";
import { fileConfig } from "../config.js";
import { hashFile, moveFile } from "../server/utils.js";
var join = (...paths) => {
  if (nodeJoin)
    return nodeJoin(...paths);
  const joined = paths.map((p) => p.trim().replace(/^[\/]*|[\/]*$/g, "")).join("/");
  return (paths[0]?.at(0) === "/" ? "/" : "") + joined;
};
var getBaseURL = (className) => join(fileConfig.get("baseURL"), className);
var AdornisFileMeta = class extends AdornisEntity {
  constructor(doc) {
    super(doc);
  }
  get completeName() {
    return `${this.fileName}.${this.extension}`;
  }
};
AdornisFileMeta._class = "FileMeta";
__decorateClass([
  validate(nonOptional()),
  Field((type) => String)
], AdornisFileMeta.prototype, "fileName", 2);
__decorateClass([
  validate(nonOptional()),
  Field((type) => Int)
], AdornisFileMeta.prototype, "size", 2);
__decorateClass([
  validate(nonOptional()),
  Field((type) => String)
], AdornisFileMeta.prototype, "mimetype", 2);
__decorateClass([
  validate(nonOptional()),
  Field((type) => String)
], AdornisFileMeta.prototype, "md5", 2);
__decorateClass([
  validate(nonOptional()),
  Field((type) => String)
], AdornisFileMeta.prototype, "extension", 2);
AdornisFileMeta = __decorateClass([
  Entity()
], AdornisFileMeta);
var AdornisFile = class extends MongoEntity {
  static get allFields() {
    return selectionSet(() => this, 2);
  }
  constructor(doc) {
    super(doc);
  }
  getFullPath() {
    if (!this.meta)
      throw new Error("cant give you a full path without having .meta set");
    return this.constructor.getFullPath(this);
  }
  static getFullPath(file) {
    if (!file.meta)
      throw new Error("cant give you a full path without having .meta set");
    return join(fileConfig.get("datadir"), this._class, this.getFileName(file));
  }
  static getFileName(file) {
    if (!file.meta)
      throw new Error("cant give you a full path without having .meta set");
    if (!file._id || !file.meta.extension)
      throw new Error("_id or extension missing, cant generate conflict free name for storage");
    return file._id + "." + file.meta.extension;
  }
  /**
   * the name of the file when serving (human readable)
   */
  static getServeName(file) {
    if (!file.meta)
      throw new Error("cant give you a serve name without having .meta set");
    if (!file.meta.fileName || !file.meta.extension)
      throw new Error("fileName or extension missing, cant generate serve name");
    return file.meta.fileName + "." + file.meta.extension;
  }
  static makeLink(type, fileID, queryParams) {
    return `${join(getBaseURL(this._class), type, fileID ?? "")}${queryParams ? `?${queryParams.toString()}` : ""}`;
  }
  getMimeType() {
    return this.meta?.mimetype;
  }
  /**
   * get a link which will download this file. If you want to open the file in the browser, use `getServeLink`
   */
  getDownloadLink(options) {
    return this.constructor.getDownloadLink(this._id, options);
  }
  /**
   * get a link which allows for display of this file in the browser. If you want to force download, use `getDownloadLink`
   */
  getServeLink(options) {
    return this.constructor.getServeLink(this._id, options);
  }
  static removeFile(id) {
    return async (gqlFields) => {
      const collection = await getRawCollection(this._collectionName);
      let file = await collection.findOne({ _id: id });
      if (!file)
        throw new Error(`No AdornisFile with id ${id} found!`);
      file = new (this.metaTreeNode.type())(file);
      await unlink(file.getFullPath());
      await MongoEntity.removeEntity(file._class, file._id)();
      return true;
    };
  }
  async upload(file, { acceptFileTypes, additionalInfo = {} } = {}) {
    await this.save();
    return await this.constructor.upload(file, {
      acceptFileTypes,
      additionalInfo,
      updateFileID: this._id
    });
  }
  static async customUploadHandler(additionalInfo, retrievedFiles, fileID) {
    throw new Error("customUploadHandler not implemented");
  }
  static canHandleFile(retrievedFile) {
    return true;
  }
  static async _setBasicFileMeta(file, path) {
    if (!path)
      path = file.getFullPath();
    const mimetype = await new Promise(
      async (resolve) => (await import("child_process")).exec(`file --mime-type -b ${path}`, (_, stdout) => resolve(stdout.trim()))
    );
    const md5 = await hashFile(path);
    const fileStat = await lstat(path);
    if (!fileStat)
      throw new Error("lstat of temporary file is falsy, should not happen");
    if (!file.meta)
      file.meta = new AdornisFileMeta({});
    file.meta.size = fileStat.size;
    file.meta.mimetype = mimetype;
    file.meta.md5 = md5;
  }
  static async _setCustomFileMeta(file, path) {
  }
  static async moveTmpFileAndGetMeta({
    tmpPath,
    file,
    additionalInfo
  }) {
    await this._setBasicFileMeta(file, tmpPath);
    await this._setCustomFileMeta(file, tmpPath);
    await this.afterUpload({ file, tmpPath, additionalInfo });
    const path = this.getFullPath(file);
    await mkdir(dirname(path), { recursive: true });
    await moveFile(tmpPath, path);
    return file;
  }
  /**
   * Called after upload, before creating the entity.
   *
   * Metadata will already be set.
   *
   * @param file preliminary file entity
   * @param tmpPath path of the temporary file
   * @param additionalInfo dictionary of info passed by the client
   */
  static async afterUpload({
    file,
    tmpPath,
    additionalInfo
  }) {
  }
  static async createFromBuffer(buffer, fileBasename, additionalInfo) {
    const fileName = fileBasename.split(".").slice(0, -1).join(".");
    const extension = fileBasename.split(".").slice(-1)[0];
    if (!extension)
      throw new Error("please include an extension in your fileBasename");
    const file = new this({
      _id: A.getGloballyUniqueID(),
      meta: new AdornisFileMeta({ fileName, extension }),
      ...additionalInfo
    });
    if (!file._id)
      file._id = A.getGloballyUniqueID();
    const tmpPath = join(fileConfig.get("tempDatadir"), this.getFileName(file));
    await mkdir(dirname(tmpPath), { recursive: true });
    await writeFile(tmpPath, buffer);
    await this.moveTmpFileAndGetMeta({ tmpPath, file, additionalInfo });
    if (!file.meta)
      throw new Error("Error while writing buffer to disk");
    await file.create();
    return file;
  }
  async replaceByBuffer(buffer, fileBasename) {
    const fileName = fileBasename.split(".").slice(0, -1).join(".");
    const extension = fileBasename.split(".").slice(-1)[0];
    if (!extension)
      throw new Error("please include an extension in your fileBasename");
    this.meta ??= new AdornisFileMeta({});
    this.meta.fileName = fileName;
    this.meta.extension = extension;
    const clazz = this.constructor;
    const tmpPath = join(fileConfig.get("tempDatadir"), clazz.getFileName(this));
    await mkdir(dirname(tmpPath), { recursive: true });
    await writeFile(tmpPath, buffer);
    await clazz.moveTmpFileAndGetMeta({ tmpPath, file: this });
    await this.save();
    return this;
  }
  static async createFromPath(path, additionalInfo) {
    const fileBasename = basename(path);
    const fileName = fileBasename.split(".").slice(0, -1).join(".");
    const extension = fileBasename.split(".").slice(-1)[0];
    if (!extension)
      throw new Error("please include an extension in your path");
    const file = new this({
      _id: A.getGloballyUniqueID(),
      meta: new AdornisFileMeta({ fileName, extension }),
      ...additionalInfo
    });
    await this.moveTmpFileAndGetMeta({ tmpPath: path, file, additionalInfo });
    if (!file.meta)
      throw new Error("Error while writing buffer to disk");
    await file.create();
    return file;
  }
  /**
   * get a link which allows for display of the given file in the browser. If you want to force download, use `getDownloadLink`
   */
  static getServeLink(fileID, options) {
    return A.absoluteUrl(
      this.makeLink("serve", fileID, options ? new URLSearchParams(Object.entries(options)) : void 0)
    );
  }
  /**
   * get a link which will download the given file. If you want to open the file in the browser, use `getServeLink`
   */
  static getDownloadLink(fileID, options) {
    return A.absoluteUrl(
      this.makeLink("download", fileID, options ? new URLSearchParams(Object.entries(options)) : void 0)
    );
  }
  static async upload(file, { updateFileID, acceptFileTypes, additionalInfo = {} } = {}) {
    const formData = new FormData();
    if (acceptFileTypes && !acceptFileTypes.split(",").includes("." + file.name.split(".").slice(-1)[0].toLowerCase()))
      throw new Error("selected file has no valid file type, was: " + file.name.split(".").slice(-1)[0].toLowerCase());
    additionalInfo.sessionID = BaseQLClient.sessionID;
    additionalInfo.name = file.name;
    console.log("upload file: ", additionalInfo);
    formData.append("_file", file);
    if (updateFileID)
      formData.append("_file_id", updateFileID);
    formData.append("_file_additional_info", JSON.stringify(additionalInfo));
    return this._uploadFormData(formData);
  }
  static _uploadFormData(formData) {
    return new Promise(async (resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", A.absoluteUrl(this.makeLink("upload")), true);
      xhr.onprogress = (e) => console.log("ON PROGRESS: ", e);
      xhr.onloadstart = (e) => console.log("ON START: ", e);
      xhr.onloadend = (e) => {
        console.log("ON END: ", e);
        const res = xhr.responseText;
        if (res.includes("<html>"))
          return reject(new Error(`Expected upload response but got html: ${res}`));
        try {
          const parsedRes = JSON.parse(res);
          if (parsedRes.status === "error")
            return reject(parsedRes);
          if (parsedRes.status === "success")
            return resolve(parsedRes.id);
          reject("unknown state in upload form data");
        } catch (error) {
          reject(error);
        }
      };
      xhr.onerror = (e) => reject(e);
      xhr.send(formData);
    });
  }
  static async _readFromPath(path, opts = {}) {
    await stat(path);
    return createReadStream(path, opts);
  }
  static async serveFile({
    request,
    response,
    fileID,
    options,
    isDownload = false
  }) {
    const fileRaw = await (await getRawCollection(this._collectionName)).findOne({ _id: fileID });
    const file = constructValue(fileRaw);
    if (!file) {
      logger.warn({ fileID }, "Trying to serve file but cannot find requested id in db");
      return response.sendStatus(404);
    }
    const path = file.getFullPath();
    try {
      if (isDownload)
        response.setHeader(
          "Content-disposition",
          "attachment; filename=" + (!file.meta ? "file" : this.getServeName(file))
        );
      return await this._serveFileFromPath({
        fileID,
        path,
        contentType: file.getMimeType() || "text/plain",
        request,
        response,
        options
      });
    } catch (err) {
      logger.warn({ fileID, path, err }, "trying to serve file but stat test failed, returning 404");
      response.sendStatus(404);
    }
  }
  static async _serveFileFromPath({
    request,
    response,
    fileID,
    path,
    contentType,
    options
  }) {
    response.set("content-type", contentType);
    pipeline(await this._readFromPath(path), response, (err) => {
      if (err)
        console.warn(`PIPELINE ERROR: (${path})`, err);
    });
  }
};
AdornisFile._collectionName = "files";
AdornisFile._class = "File";
/** if set to true the filesRouter will use the static customUploadHandler function instead of creating a file instency for you */
AdornisFile.customUpload = false;
__decorateClass([
  Field((type) => AdornisFileMeta)
], AdornisFile.prototype, "meta", 2);
__decorateClass([
  Field((type) => Date, { default: (v) => v ?? /* @__PURE__ */ new Date() })
], AdornisFile.prototype, "createdAt", 2);
__decorateClass([
  onlyServer
], AdornisFile.prototype, "getFullPath", 1);
__decorateClass([
  onlyClient
], AdornisFile.prototype, "upload", 1);
__decorateClass([
  onlyServer
], AdornisFile.prototype, "replaceByBuffer", 1);
__decorateClass([
  onlyServer
], AdornisFile, "getFullPath", 1);
__decorateClass([
  onlyServer
], AdornisFile, "getFileName", 1);
__decorateClass([
  Mutation((type) => Boolean),
  __decorateParam(0, Arg("id", (type) => String))
], AdornisFile, "removeFile", 1);
__decorateClass([
  onlyServer
], AdornisFile, "customUploadHandler", 1);
__decorateClass([
  onlyServer
], AdornisFile, "canHandleFile", 1);
__decorateClass([
  onlyServer
], AdornisFile, "_setBasicFileMeta", 1);
__decorateClass([
  onlyServer
], AdornisFile, "_setCustomFileMeta", 1);
__decorateClass([
  onlyServer
], AdornisFile, "moveTmpFileAndGetMeta", 1);
__decorateClass([
  onlyServer
], AdornisFile, "createFromBuffer", 1);
__decorateClass([
  onlyServer
], AdornisFile, "createFromPath", 1);
__decorateClass([
  onlyClient
], AdornisFile, "upload", 1);
__decorateClass([
  onlyClient
], AdornisFile, "_uploadFormData", 1);
__decorateClass([
  onlyServer
], AdornisFile, "_readFromPath", 1);
__decorateClass([
  onlyServer
], AdornisFile, "serveFile", 1);
__decorateClass([
  onlyServer
], AdornisFile, "_serveFileFromPath", 1);
AdornisFile = __decorateClass([
  Entity()
], AdornisFile);
var dataURLtoFile = (dataurl, filename) => {
  var arr = dataurl.split(","), mime = arr[0].match(/:(.*?);/)?.[1], bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};
export {
  AdornisFile,
  AdornisFileMeta,
  dataURLtoFile,
  getBaseURL
};
